import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiChevronLeft, FiChevronRight, FiEye, FiPause, FiPlay, FiTrash2, FiUserPlus,
  FiVolume2, FiVolumeX, FiX,
} from 'react-icons/fi';
import { cDeleteStory, cStoryViewers, cViewStory, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import { isDirectVideo, parseVideo } from '../../utils/video';
import { claimPlayback, playClip, releasePlayback, useVideoSound } from '../../utils/videoSound';
import UserAvatar from './UserAvatar';
import { BLUE, timeAgo } from './brand';

const DURATION = 5000;                    // photos / text are shown for 5 seconds

/**
 * Full-screen story viewer (Facebook style): progress bars per story, tap the
 * right side for the next one, the left side for the previous one, keyboard
 * arrows work too. Own stories show the viewer list and a delete button.
 */
export default function StoryViewer({ groups, startIndex = 0, onClose, onDelete, onCreate }) {
  const { t, lang } = useLanguage();
  const { user } = useCommunity();
  const navigate = useNavigate();
  const [gi, setGi] = useState(startIndex);
  const [si, setSi] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [viewers, setViewers] = useState(null);
  const [clipMs, setClipMs] = useState(DURATION);   // video clips play for their own length
  const [soundOn, setSoundOn] = useVideoSound();    // shared with the feed + Reels
  const [soundBlocked, setSoundBlocked] = useState(false);   // browser refused sound
  const muted = !soundOn;
  const timer = useRef(null);
  const tick = useRef(null);

  const group = groups[gi];
  const story = group?.stories?.[si];
  const mine = !!(user && group && group.user_id === user.id);
  const isClip = story?.kind === 'video_file';     // a video the author uploaded
  const span = isClip ? clipMs : DURATION;

  const next = useCallback(() => {
    const g = groups[gi];
    if (!g) { onClose(); return; }
    if (si + 1 < g.stories.length) { setSi(si + 1); return; }
    if (gi + 1 < groups.length) { setGi(gi + 1); setSi(0); return; }
    onClose();
  }, [gi, si, groups, onClose]);

  const prev = useCallback(() => {
    if (si > 0) { setSi(si - 1); return; }
    if (gi > 0) {
      const g = groups[gi - 1];
      setGi(gi - 1);
      setSi(Math.max(0, (g.stories?.length || 1) - 1));
    }
  }, [gi, si, groups]);

  // Mark the story as seen.
  useEffect(() => {
    if (!story) return;
    cViewStory(story.id).catch(() => {});
  }, [story]);

  // Progress + auto advance (video clips use their own duration).
  useEffect(() => {
    setProgress(0);
    clearInterval(tick.current);
    clearTimeout(timer.current);
    if (!story || paused) return undefined;
    const started = Date.now();
    tick.current = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - started) / span) * 100));
    }, 60);
    timer.current = setTimeout(next, span);
    return () => { clearInterval(tick.current); clearTimeout(timer.current); };
  }, [story, paused, next, span]);

  // A fresh story starts its own countdown (a video clip sets the real length).
  useEffect(() => { setClipMs(DURATION); setSoundBlocked(false); }, [story]);

  // Keyboard controls.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === ' ') { e.preventDefault(); setPaused((p) => !p); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose]);

  if (!group || !story) return null;

  const author = group.author || {};

  /** Open the author's profile (closing the viewer first). */
  const goProfile = () => {
    if (!author?.username) return;
    if (onClose) onClose();
    navigate(`/u/${author.username}`);
  };

  const toggleSound = () => setSoundOn(!soundOn);   // remembered for every video

  const deleteStory = async () => {
    if (!window.confirm(t('deleteStoryConfirm'))) return;
    try {
      await cDeleteStory(story.id);
      toast.success(t('deletedOk'));
      if (onDelete) onDelete(story.id);
      setSi(0);
      if (gi + 1 < groups.length) setGi(gi + 1); else onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const showViewers = async () => {
    try {
      setViewers(await cStoryViewers(story.id));
    } catch (e) {
      toast.error(t('somethingWrong'));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="relative w-full h-full max-w-[460px] mx-auto bg-black sm:rounded-2xl overflow-hidden">
        {/* progress bars */}
        <div className="absolute top-2 left-2 right-2 z-30 flex gap-1">
          {group.stories.map((s, i) => (
            <span key={s.id} className="flex-1 h-[3px] rounded-full bg-white/35 overflow-hidden">
              <span
                className="block h-full bg-white ease-linear"
                style={{ width: i < si ? '100%' : i === si ? `${progress}%` : '0%', transition: 'width .12s linear' }}
              />
            </span>
          ))}
        </div>

        {/* header — the author opens their profile */}
        <div className="absolute top-5 left-2 right-2 z-30 flex items-center gap-2 pt-2 anim-fade-in">
          <button
            type="button"
            onClick={goProfile}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            title={t('viewProfile')}
          >
            <UserAvatar user={author} size={36} />
            <span className="min-w-0">
              <span className="block text-white text-sm font-bold truncate">{author.name || author.username}</span>
              <span className="block text-white/70 text-[11px]">
                {timeAgo(story.created_at, lang)} · <span className="underline">{t('viewProfile')}</span>
              </span>
            </span>
          </button>

          {isClip && (
            <button
              type="button"
              onClick={() => { setSoundBlocked(false); toggleSound(); }}
              className="p-1.5 rounded-full text-white/90 hover:bg-white/15"
              aria-label={muted ? t('unmute') : t('mute')}
              title={muted ? t('storyVideoMuted') : t('mute')}
            >
              {muted ? <FiVolumeX className="w-5 h-5" /> : <FiVolume2 className="w-5 h-5" />}
            </button>
          )}

          {mine && (
            <>
              <button
                type="button"
                onClick={showViewers}
                className="inline-flex items-center gap-1 text-white/90 text-xs font-bold bg-white/15 px-2 py-1 rounded-full"
                title={t('viewers')}
              >
                <FiEye className="w-4 h-4" /> {story.views || 0}
              </button>
              <button type="button" onClick={deleteStory} className="p-1.5 rounded-full text-white/90 hover:bg-white/15" aria-label={t('delete')}>
                <FiTrash2 className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="p-1.5 rounded-full text-white/90 hover:bg-white/15"
            aria-label="Pause"
          >
            {paused ? <FiPlay className="w-5 h-5" /> : <FiPause className="w-5 h-5" />}
          </button>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full text-white/90 hover:bg-white/15" aria-label={t('close')}>
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* content */}
        {story.kind === 'text' ? (
          <div
            className="absolute inset-0 flex items-center justify-center p-10"
            style={{ background: story.background || 'linear-gradient(135deg, #0B5FDB, #083E93)' }}
          >
            <p className="text-white text-2xl font-extrabold text-center whitespace-pre-wrap break-words leading-snug">
              {story.text}
            </p>
          </div>
        ) : story.kind === 'video_file' ? (
          <StoryClip
            url={fullUrl(story.media)}
            poster={story.thumb ? fullUrl(story.thumb) : undefined}
            paused={paused}
            muted={muted}
            onBlocked={() => setSoundBlocked(true)}
            onDuration={(d) => setClipMs(Math.min(30000, Math.max(3000, Math.round((d || 5) * 1000))))}
          />
        ) : story.kind === 'video' ? (
          <StoryVideo url={story.media} />
        ) : (
          <img src={fullUrl(story.media)} alt="story" className="absolute inset-0 w-full h-full object-contain anim-fade-in" />
        )}

        {/* “tap to turn the sound on” — sound off, or the browser blocked it */}
        {isClip && (muted || soundBlocked) && (
          <button
            type="button"
            data-sfx="none"
            onClick={() => { setSoundBlocked(false); toggleSound(); }}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-[11px] font-bold anim-pop"
            style={{ background: BLUE }}
          >
            <FiVolumeX className="w-3.5 h-3.5" /> {t('storyVideoMuted')}
          </button>
        )}


        {/* caption on photo / video stories */}
        {story.kind !== 'text' && story.text && (
          <p className="absolute bottom-16 left-4 right-4 z-20 text-white text-sm font-semibold bg-black/40 rounded-xl px-3 py-2">
            {story.text}
          </p>
        )}

        {/* tap zones */}
        <button
          type="button"
          data-sfx="blip"
          onClick={prev}
          className="absolute left-0 top-0 bottom-0 w-1/4 z-20 flex items-center justify-start pl-1 text-white/60 hover:text-white transition"
          aria-label="Previous"
        >
          <FiChevronLeft className="w-7 h-7" />
        </button>
        <button
          type="button"
          data-sfx="blip"
          onClick={next}
          className="absolute right-0 top-0 bottom-0 w-1/4 z-20 flex items-center justify-end pr-1 text-white/60 hover:text-white transition"
          aria-label="Next"
        >
          <FiChevronRight className="w-7 h-7" />
        </button>

        {mine && (
          <button
            type="button"
            onClick={onCreate}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-bold"
            style={{ background: BLUE }}
          >
            <FiUserPlus className="w-4 h-4" /> {t('createStory')}
          </button>
        )}

        {viewers && (
          <div className="absolute inset-0 z-40 bg-black/85 flex flex-col" onClick={() => setViewers(null)}>
            <div className="p-4 border-b border-white/15 flex items-center justify-between">
              <h4 className="text-white font-bold text-sm">
                {t('viewers')} · {viewers.count}
              </h4>
              <button type="button" className="text-white/80" onClick={() => setViewers(null)} aria-label={t('close')}>
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
              {(!viewers.users || viewers.users.length === 0) && (
                <p className="text-white/60 text-sm">{t('noStories')}</p>
              )}
              {(viewers.users || []).map((v) => (
                <div key={v.id} className="flex items-center gap-3">
                  <UserAvatar user={v} size={36} to={`/u/${v.username}`} />
                  <span className="text-white text-sm font-semibold truncate">{v.name || v.username}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A **video the author uploaded to a story** (compressed MP4 + poster frame).
 * It fills the story frame, plays while the story is running and pauses with it.
 * Sound follows the shared video preference (on by default).
 */
function StoryClip({ url, poster, paused, muted, onDuration, onBlocked }) {
  const ref = useRef(null);

  // Keep the element in step with the story pause button (⏸ / ▶) and the sound switch.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (paused) { el.pause(); releasePlayback(el); return; }
    claimPlayback(el);
    playClip(el, !muted).then((st) => { if (st === 'blocked' && onBlocked) onBlocked(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, muted, url]);

  useEffect(() => () => releasePlayback(ref.current), []);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={ref}
      src={url}
      poster={poster}
      playsInline
      preload="metadata"
      onLoadedMetadata={(e) => onDuration && onDuration(e.target.duration)}
      className="absolute inset-0 w-full h-full object-contain bg-black anim-fade-in"
    />
  );
}

/** A video story: a direct file plays in a <video>, a link plays in an iframe. */
function StoryVideo({ url }) {
  if (isDirectVideo(url)) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video src={url} controls autoPlay playsInline className="absolute inset-0 w-full h-full object-contain bg-black" />
    );
  }
  const video = parseVideo(url);
  if (!video.embed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0B5FDB] to-[#083E93]">
        <a href={url} target="_blank" rel="noreferrer" className="text-white underline text-sm break-all px-6 text-center">
          {url}
        </a>
      </div>
    );
  }
  return (
    <iframe
      title="story video"
      src={video.autoplay || video.embed}
      className="absolute inset-0 w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
    />
  );
}

